/**
 * Clase utilitaria para manejo de fechas y horas con timezone
 * Especialmente diseñada para trabajar con inputs de fecha + hora AM/PM por separado
 */
export class DateTimeHelper {
  /**
   * Convierte una hora en formato AM/PM a formato 24 horas
   * @param timeAMPM - Hora en formato "5:00 PM", "12:00 AM", etc.
   * @returns String en formato "HH:mm" (24 horas) o null si es inválido
   */
  static convertAMPMto24Hour(timeAMPM: string): string | null {
    if (!timeAMPM || typeof timeAMPM !== 'string') {
      return null;
    }

    try {
      // Limpiar el string y dividir
      const cleanTime = timeAMPM.trim().toUpperCase();
      const isAM = cleanTime.includes('AM');
      const isPM = cleanTime.includes('PM');

      if (!isAM && !isPM) {
        return null; // No es formato AM/PM válido
      }

      // Extraer la parte de tiempo (antes del AM/PM)
      const timePart = cleanTime.replace(/\s*(AM|PM)/, '').trim();
      const [hourStr, minuteStr] = timePart.split(':');

      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      // Validar valores
      if (isNaN(hour) || isNaN(minute) || hour < 1 || hour > 12 || minute < 0 || minute > 59) {
        return null;
      }

      // Convertir a formato 24 horas
      if (isAM) {
        // AM: 12:xx AM = 00:xx, resto queda igual
        if (hour === 12) {
          hour = 0;
        }
      } else {
        // PM: 12:xx PM = 12:xx, resto + 12
        if (hour !== 12) {
          hour += 12;
        }
      }

      // Formatear con ceros a la izquierda
      const hourStr24 = hour.toString().padStart(2, '0');
      const minuteStr24 = minute.toString().padStart(2, '0');

      return `${hourStr24}:${minuteStr24}`;
    } catch (error) {
      console.error('Error converting AM/PM time:', error);
      return null;
    }
  }

  /**
   * Combina una fecha y una hora AM/PM en una sola fecha local
   * @param date - Fecha base (Date object)
   * @param timeAMPM - Hora en formato AM/PM ("5:00 PM")
   * @returns Nueva fecha combinada en timezone local del usuario
   */
  static combineDateAndTime(date: Date, timeAMPM: string): Date | null {
    if (!date || !timeAMPM) {
      return null;
    }

    // Convertir AM/PM a formato 24 horas
    const time24h = this.convertAMPMto24Hour(timeAMPM);
    if (!time24h) {
      console.error(`No se pudo convertir la hora: ${timeAMPM}`);
      return null;
    }

    const [hours, minutes] = time24h.split(':').map(num => parseInt(num, 10));

    // Crear nueva fecha usando los componentes de la fecha original + la hora convertida
    const combinedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      0, // segundos
      0 // milisegundos
    );

    return combinedDate;
  }

  /**
   * Extrae la hora de una fecha UTC y la convierte a formato AM/PM local
   * @param date - Fecha en UTC (generalmente del backend)
   * @returns String en formato AM/PM ("5:00 PM") en timezone local
   */
  static extractTimeToAMPM(date: Date): string | null {
    if (!date) {
      return null;
    }

    try {
      // Convertir de UTC a local timezone
      const localDate = new Date(date);
      let hours = localDate.getHours();
      const minutes = localDate.getMinutes();

      // Determinar AM/PM
      const ampm = hours >= 12 ? 'PM' : 'AM';

      // Convertir a formato 12 horas
      if (hours === 0) {
        hours = 12; // Medianoche = 12 AM
      } else if (hours > 12) {
        hours -= 12; // Convertir PM
      }

      // Formatear
      const hourStr = hours.toString();
      const minuteStr = minutes.toString().padStart(2, '0');

      return `${hourStr}:${minuteStr} ${ampm}`;
    } catch (error) {
      console.error('Error extracting time to AM/PM:', error);
      return null;
    }
  }

  /**
   * Convierte una fecha local a UTC para enviar al backend
   * @param localDateTime - Fecha en timezone local
   * @returns String ISO en UTC para el backend
   */
  static toUTCForBackend(localDateTime: Date): string | null {
    if (!localDateTime) {
      return null;
    }

    try {
      const utcString = localDateTime.toISOString();
      return utcString;
    } catch (error) {
      console.error('Error converting to UTC:', error);
      return null;
    }
  }

  /**
   * Extrae solo la fecha (sin hora) de un Date object
   * @param date - Fecha original
   * @returns Nueva fecha con hora en 00:00:00
   */
  static extractDateOnly(date: Date): Date | null {
    if (!date) {
      return null;
    }

    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
